import { IUser } from '@interfaces/user.interface';
import { personnelReportRequest } from '@shared/services/report.service';
import { userOptionRequest } from '@shared/services/user.service';
import { LicenseTypes, States } from '@shared/utils/enums';
import { exportJsonToCsv, getLicenseTypesText, getStateName, sortStringColumn } from '@shared/utils/functions';
import { Button, DatePicker, message, Select, Table } from 'antd';
import { Content } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa6';
import { MdOutlineQueryStats } from 'react-icons/md';

const { RangePicker } = DatePicker;

const columns = [
  { title: <span className='text-sm'>Agent Name</span>, width: 210, dataIndex: 'agentname', key: 'agentname', sorter: sortStringColumn('agentname') },
  { title: <span className='text-sm'>MLS ID</span>, width: 110, dataIndex: 'mlsid', key: 'mlsid' },
  { title: <span className='text-sm'>MLS Name</span>, width: 110, dataIndex: 'mlsname', key: 'mlsname' },
  { title: <span className='text-sm'>NARS #</span>, width: 110, dataIndex: 'realtor', key: 'realtor' },
  { title: <span className='text-sm'>Name on License</span>, width: 210, dataIndex: 'licensename', key: 'licensename' },
  { title: <span className='text-sm'>State</span>, width: 130, dataIndex: 'state', key: 'state', render: (value: string) => getStateName(value) },
  { title: <span className='text-sm'>License #</span>, width: 150, dataIndex: 'number', key: 'number' },
  { title: <span className='text-sm'>License Type</span>, width: 160, dataIndex: 'type', key: 'type', render: (value: string) => getLicenseTypesText(value as LicenseTypes) },
  { title: <span className='text-sm'>Issue Date</span>, width: 120, dataIndex: 'issuedate', key: 'issuedate', render: (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '-' },
  { title: <span className='text-sm'>Expire Date</span>, width: 120, dataIndex: 'expiredate', key: 'expiredate', render: (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '-' },
];

const Personnel = () => {
  const [messageApi, contextHolder] = message.useMessage();
  
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [expireDates, setExpireDates] = useState<any[]>();
  const [results, setResults] = useState<any[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<number[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [agents, setAgents] = useState<IUser[]>([]);

  useMemo(() => {
    const fetchData = async () => {
      const options = await userOptionRequest();
      setAgents( options.response.users.map((user: any) => ({ value: user.id, label: user.fullName })).sort((a: any, b: any) => a.label.localeCompare(b.label)));

      setLoading(false);
    };

    fetchData();
  }, []); 


  const _OnRunReport = () => {
    setFetching(true);

    personnelReportRequest({ expireDates, agents: selectedAgents, states: selectedStates, types: selectedTypes })
      .then((res: any) => {
        setResults(res.response);
        setFetching(false);
      });
  };

  const _OnExportReport = () => {
    if(results.length > 0) {
      const csvData = results.map(({ __typename, type, issuedate, expiredate, ...rest }) => ({ ...rest, type: getLicenseTypesText(type as LicenseTypes) , issuedate: issuedate ? dayjs(issuedate).format('MM/DD/YYYY') : '', expiredate: expiredate ? dayjs(expiredate).format('MM/DD/YYYY') : '' }));
      exportJsonToCsv(csvData, `Licensing_Report_${new Date().toISOString().replace(/[-:.TZ]/g, '')}`, ['Agent Name', 'MLS ID', 'MLS Name', 'NARS #', 'Name on License', 'State', 'License #', 'License Type', 'Issue Date', 'Expire Date']);
      messageApi.open({ type: 'success', content: 'Report data exported correctly' });
    } else {
      messageApi.open({ type: 'error', content: 'There are no records to export' });
    }
  };

  return (
    <>
      {contextHolder}
      <Content className='px-4 h-[97%]'>
        <div className='w-full grid grid-cols-12 grid-rows-2 gap-4 mb-4'>
          <div className='col-span-3 flex items-center'>
            <div className='whitespace-nowrap mr-2'>Expire Date: </div>
            <RangePicker
              format='MM/DD/YYYY'
              className='w-full'
              placeholder={['Start Date', 'End Date']} 
              onChange={(dates) => { dates ? setExpireDates([dayjs(dates[0]).startOf('day').toDate(), dayjs(dates[1]).endOf('day').toDate()]) : setExpireDates([]) }}
            />
          </div>
          <div className='col-span-6 flex items-center'>
            <div className='mr-2 whitespace-nowrap'>Agent: </div>
            <Select
                mode='multiple'
                showSearch
                placeholder='Select an agent'
                optionFilterProp='label'
                className='w-full mr-8'
                maxTagCount='responsive'
                loading={loading}
                options={agents}
                value={selectedAgents}
                onChange={setSelectedAgents}
              />
          </div>
          <div className='col-span-3 flex items-center justify-end'>
            <Button className='rounded-xl' onClick={_OnRunReport} icon={<MdOutlineQueryStats size={20} className='mt-1' />}>Run Report</Button>
            <Button className='rounded-xl ml-2' onClick={_OnExportReport} icon={<FaFileCsv size={16} className='mt-1' />}>Export</Button>
          </div>
          <div className='col-span-3 flex items-center'>
            <div className='mr-2 whitespace-nowrap'>License Type: </div>
            <Select
              mode='multiple'
              showSearch
              placeholder='Select a type'
              optionFilterProp='label'
              className='w-full'
              loading={loading}
              maxTagCount='responsive'
              options={Object.values(LicenseTypes).map((val) => ({ value: val, label: getLicenseTypesText(val) }))}
              value={selectedTypes}
              onChange={setSelectedTypes}
            />
          </div>
          <div className='col-span-3 flex items-center'>
            <div className='mr-2'>State: </div>
            <Select
              mode='multiple'
              showSearch
              placeholder='Select a state'
              optionFilterProp='label'
              className='w-full mr-8'
              loading={loading}
              maxTagCount='responsive'
              options={Object.values(States).map((val) => ({ value: val, label: getStateName(val) }))}
              value={selectedStates}
              onChange={setSelectedStates}
            />
          </div>
          <div className='col-span-6'></div>
        </div>
        <div className='h-[calc(100vh-280px)]'>
          <Table
            columns={columns}
            loading={fetching}
            size='small'
            pagination={{ 
              pageSizeOptions: ['25', '50', '100'], 
              defaultPageSize: 50,
              total: results.length,
              showTotal: (total) => `${total} Results`,
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 420px)' }}
            rowClassName={() => 'font-row-sm'}
            dataSource={results}
          />
        </div>
      </Content>
    </>
  );
};

export default Personnel;